import React from 'react';
import { CheckCircle, Circle, AlertTriangle } from 'lucide-react';

interface DeadlineProgressProps {
  total: number;
  completed: number;
  overdue: number;
  label?: string;
  showPercentage?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function DeadlineProgress({ 
  total, 
  completed, 
  overdue,
  label = 'Deadline Progress',
  showPercentage = true,
  size = 'md'
}: DeadlineProgressProps) {
  // Ensure all values are valid numbers to prevent NaN
  const safeTotal = isNaN(total) || total < 0 ? 0 : total;
  const safeCompleted = isNaN(completed) || completed < 0 ? 0 : completed;
  const safeOverdue = isNaN(overdue) || overdue < 0 ? 0 : overdue;
  
  const remaining = Math.max(0, safeTotal - safeCompleted - safeOverdue);
  const completedPercentage = safeTotal > 0 ? Math.round((safeCompleted / safeTotal) * 100) : 0;
  const overduePercentage = safeTotal > 0 ? Math.round((safeOverdue / safeTotal) * 100) : 0;
  const remainingPercentage = safeTotal > 0 ? Math.round((remaining / safeTotal) * 100) : 0;

  const sizeClasses = {
    sm: {
      container: 'h-2',
      text: 'text-xs',
      icon: 'w-4 h-4',
    },
    md: {
      container: 'h-3',
      text: 'text-sm',
      icon: 'w-5 h-5',
    },
    lg: {
      container: 'h-4',
      text: 'text-base',
      icon: 'w-6 h-6',
    },
  };

  const classes = sizeClasses[size];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className={`font-medium text-gray-700 ${classes.text}`}>{label}</span>
        {showPercentage && (
          <span className={`text-gray-500 ${classes.text}`}>
            {completedPercentage}% Complete
          </span>
        )}
      </div>

      <div className="relative">
        <div className={`w-full bg-gray-200 rounded-full overflow-hidden ${classes.container}`}>
          <div className="flex h-full">
            {safeCompleted > 0 && (
              <div
                className="bg-green-500 transition-all duration-300 ease-out"
                style={{ width: `${completedPercentage}%` }}
              />
            )}
            {safeOverdue > 0 && (
              <div
                className="bg-red-500 transition-all duration-300 ease-out"
                style={{ width: `${overduePercentage}%` }}
              />
            )}
            {remaining > 0 && (
              <div
                className="bg-blue-500 transition-all duration-300 ease-out"
                style={{ width: `${remainingPercentage}%` }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <CheckCircle className="w-3 h-3 text-green-500 mr-1" />
            <span className="text-gray-600">{safeCompleted} Completed</span>
          </div>
          {safeOverdue > 0 && (
            <div className="flex items-center">
              <AlertTriangle className="w-3 h-3 text-red-500 mr-1" />
              <span className="text-gray-600">{safeOverdue} Overdue</span>
            </div>
          )}
          <div className="flex items-center">
            <Circle className="w-3 h-3 text-blue-500 mr-1" />
            <span className="text-gray-600">{remaining} Remaining</span>
          </div>
        </div>
      </div>
    </div>
  );
}